/** Assistant chat bubble with exports (md/csv/pdf/docx/txt) and feedback. */
import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { Download, Loader2 } from "lucide-react";
import { DevAgentChatMessageContent } from "../chat/DevAgentChatMessageContent";
import { extractTableFromMessage } from "./feedExtractTable";
import { FeedbackButtons, CopyButton } from "./feedChatPrimitives";
import { useDownloadAction } from "../download/useDownloadAction";
import { findStructuredToolCall } from "../download/findStructuredToolCall";
import { api, devAgentAuthHeaders } from "../devAgentService/core";
// Static imports — were dynamic `await import(...)` previously, but Monaco's
// AMD loader (loaded by MetadataEditorPanel) hijacks `window.define` and any
// runtime dynamic import then collides with "Can only have one anonymous
// define call per script file". Static imports bundle at build time and
// bypass the runtime loader entirely.
import {
  downloadRawText,
  exportToCSV,
  exportToPDF,
  exportToDOCX,
} from "../exportHandlers";

const DOWNLOAD_FORMATS = ["md", "csv", "pdf", "docx", "txt"] as const;
type DownloadFormat = (typeof DOWNLOAD_FORMATS)[number];

const COLOR_BY_FMT: Record<string, string> = {
  md: "hover:text-foreground",
  csv: "hover:text-emerald-400",
  pdf: "hover:text-rose-400",
  docx: "hover:text-indigo-400",
  txt: "hover:text-foreground",
};

export interface BlockedAction {
  /** Server-defined action kind. Today only `trigger_rag_ingest` is wired. */
  kind: string;
  /** Button label text. */
  label: string;
  /** Backend POST endpoint to call when the button is clicked. */
  endpoint?: string;
  /** Whether the button is currently actionable. */
  enabled?: boolean;
}

export function FeedChatAgent({
  content,
  itemId,
  timestamp,
  onFeedback,
  readOnly,
  toolCalls,
  messageUuid,
  blockedAction,
}: {
  content: string;
  itemId: string;
  timestamp?: number;
  onFeedback?: (id: string, q: "good" | "bad") => void;
  readOnly?: boolean;
  toolCalls?: unknown[];
  messageUuid?: string;
  blockedAction?: BlockedAction;
}) {
  if (!content || !content.trim()) return null;
  const [actionBusy, setActionBusy] = useState(false);

  /** Click handler for the inline blocked-state button (e.g. "Start indexing").
   *  Server re-validates state on every call, so we just post and surface the
   *  response message. The existing IngestOngoingJobSync polling picks up the
   *  job_id and ActivityDropdown takes over the progress display. */
  const handleBlockedAction = useCallback(async () => {
    if (!blockedAction?.endpoint || actionBusy) return;
    setActionBusy(true);
    try {
      await api.post(blockedAction.endpoint, undefined, { headers: devAgentAuthHeaders() });
      toast.success("Indexing started");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        err?.message ||
        "Failed to start the action";
      toast.error(msg);
    } finally {
      setActionBusy(false);
    }
  }, [blockedAction, actionBusy]);

  // Server-side download path: re-runs the SOQL/MCP tool, streams formatted file.
  const { downloadingFormat, download: serverDownload } = useDownloadAction(
    toolCalls,
    content,
    messageUuid,
  );
  // Client-side fallback path: parses the chat content (used for md/txt and
  // for the rare case where there's no SOQL/tool to re-run server-side).
  const [clientDownloading, setClientDownloading] = useState<string | null>(null);
  const downloading: string | null = downloadingFormat || clientDownloading;

  // Bottom-row downloads — ALWAYS client-side. Just dump whatever's in the
  // chat message text. For "full results" the user clicks the separate
  // "Download full results" row above, which hits the server-side endpoint.
  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      if (downloading) return;
      try {
        const baseName = `response-${Date.now()}`;
        setClientDownloading(format);

        // Plain text formats — dump the raw assistant message
        if (format === "md" || format === "txt") {
          const mime = format === "md" ? "text/markdown" : "text/plain";
          downloadRawText(content, `${baseName}.${format}`, mime);
          const sizeKB = (content.length / 1024).toFixed(1);
          toast.success(`Downloaded .${format} — ${sizeKB} KB`);
          return;
        }

        // CSV / PDF / DOCX — parse a table out of the chat content
        const table = extractTableFromMessage(content);
        if (!table || table.headers.length === 0) {
          toast.error("No table in this message — try the 'Download full results' row above");
          return;
        }
        if (format === "csv") {
          exportToCSV(table, `${baseName}.csv`);
          toast.success(`Downloaded CSV — ${table.rows.length} rows`);
        } else if (format === "pdf") {
          exportToPDF(table, `${baseName}.pdf`, "OrgCompass Report");
          toast.success(`Downloaded PDF — ${table.rows.length} rows`);
        } else if (format === "docx") {
          await exportToDOCX(table, `${baseName}.docx`, "OrgCompass Report");
          toast.success(`Downloaded DOCX — ${table.rows.length} rows`);
        }
      } catch (err: unknown) {
        const msg =
          err && typeof err === "object" && "message" in err
            ? String((err as { message?: string }).message)
            : "unknown error";
        toast.error(`Download failed: ${msg}`);
        console.warn("[FeedChatAgent] download failed:", err);
      } finally {
        setClientDownloading(null);
      }
    },
    [content, downloading],
  );

  // "Full results" downloads — server-side. Hits /download-soql or
  // /download-tool-result, re-runs the original SOQL/MCP tool, returns a
  // streamed file with the COMPLETE result set. Only enabled when the
  // message has a structured tool call we can re-run.
  const handleFullDownload = useCallback(
    async (format: "csv" | "pdf" | "docx") => {
      if (downloading) return;
      await serverDownload(format);
    },
    [downloading, serverDownload],
  );

  const _tc = Array.isArray(toolCalls) && toolCalls.length > 0 ? findStructuredToolCall(toolCalls, "") : null;
  const hasDownloadableTool = _tc !== null && ["run_soql_query", "tooling_query"].includes(_tc.tool);

  return (
    <div className="py-1.5">
      <div className="px-4 py-2.5 max-w-full text-sm text-foreground select-text group/msg relative">
        <DevAgentChatMessageContent content={content} toolCalls={toolCalls} messageUuid={messageUuid} />
        {/* Blocked-state action — only renders when the assistant message
            carries `metadata.blocked_action` from the backend (e.g. RAG
            "Start indexing" / "Retry indexing"). Server-rendered metadata
            keeps the frontend dumb: backend decides label, endpoint, and
            whether to show a button at all. */}
        {blockedAction && blockedAction.endpoint && blockedAction.enabled !== false && (
          <button
            type="button"
            onClick={handleBlockedAction}
            disabled={actionBusy}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary/15 text-primary hover:bg-primary/25 border border-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionBusy && <Loader2 className="w-3 h-3 animate-spin" />}
            <span>{blockedAction.label}</span>
          </button>
        )}
        {timestamp && (
          <div className="text-[10px] text-muted-foreground/80 mt-1">
            {new Date(timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        )}
      </div>
      {!readOnly && content.length > 30 && (
        <>
          {/* "Full results" row — server-side, re-runs the tool, returns the
              COMPLETE list. Only shown when the message has a structured tool
              call we can re-run (run_soql_query, tooling_query, list_metadata,
              graph_*, etc.). Hidden otherwise. */}
          {hasDownloadableTool && (
            <div className="flex flex-wrap items-center gap-1 mt-1 ml-1">
              <span className="text-[10px] text-muted-foreground/85 mr-1 self-center italic">
                Download:
              </span>
              {(["csv", "pdf", "docx"] as const).map((fmt) => {
                const isLoading = downloadingFormat === fmt;
                const isDisabled = downloading !== null;
                return (
                  <button
                    key={`full-${fmt}`}
                    onClick={() => handleFullDownload(fmt)}
                    disabled={isDisabled}
                    className={`inline-flex items-center gap-1 p-1 px-2 rounded text-muted-foreground/75 transition-colors text-[11px] whitespace-nowrap ${
                      isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : `hover:bg-muted/50 ${COLOR_BY_FMT[fmt]}`
                    }`}
                    title={`Download full results as .${fmt}`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3 flex-shrink-0" />
                    )}
                    <span>.{fmt}</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Bottom row — only shown when full-results download is NOT
              available (hasDownloadableTool=false). Provides client-side
              save options for messages that have no structured tool to
              re-run. When full results IS available, the row above is the
              single download path. Feedback buttons always show. */}
          <div className="flex flex-wrap items-center gap-1 mt-1 ml-1">
            {onFeedback && <FeedbackButtons itemId={itemId} onFeedback={onFeedback} />}
            {!hasDownloadableTool && (
              <>
                <CopyButton text={content} />
                {DOWNLOAD_FORMATS.map((fmt) => {
                  const isLoading = clientDownloading === fmt;
                  const isDisabled = downloading !== null;
                  return (
                    <button
                      key={fmt}
                      onClick={() => handleDownload(fmt)}
                      disabled={isDisabled}
                      className={`inline-flex items-center gap-1 p-1 px-2 rounded text-muted-foreground/75 transition-colors text-[11px] whitespace-nowrap ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : `hover:bg-muted/50 ${COLOR_BY_FMT[fmt]}`
                      }`}
                      title={`Download as .${fmt}`}
                    >
                      {isLoading ? (
                        <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" />
                      ) : (
                        <Download className="w-3 h-3 flex-shrink-0" />
                      )}
                      <span>.{fmt}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}